import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, X, RefreshCw } from 'lucide-react';

/**
 * Shows a toast when a new service worker is installed and waiting to activate.
 * Clicking "Update now" → postMessage({type:'SKIP_WAITING'}) → SW activates →
 * index.js's controllerchange handler reloads the page.
 */
export default function UpdateAvailableToast() {
  const [visible, setVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  const workerRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      workerRef.current = event.detail?.worker || null;
      setVisible(true);
    };
    window.addEventListener('sw-update-ready', handler);
    return () => window.removeEventListener('sw-update-ready', handler);
  }, []);

  const applyUpdate = () => {
    setUpdating(true);
    const worker = workerRef.current;
    if (worker) {
      worker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // Fallback — just reload
      window.location.reload();
    }
  };

  const dismiss = () => setVisible(false);

  if (!visible || typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="sw-update-toast"
      className="fixed top-4 left-1/2 -translate-x-1/2 max-w-[94vw] w-auto rounded-full shadow-2xl animate-fade-slide-down flex items-center gap-2 pl-3 pr-1.5 py-1.5"
      style={{
        zIndex: 10003,
        backgroundColor: '#1A2E2E',
        border: '1px solid rgba(139,92,246,0.45)',
      }}
    >
      <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#8B5CF6' }}>
        <Sparkles className="w-3.5 h-3.5 text-white" />
      </span>
      <span className="text-xs font-bold whitespace-nowrap" style={{ fontFamily: 'Manrope', color: '#F4F1EB' }}>
        New version available
      </span>
      <span className="hidden sm:inline text-xs" style={{ fontFamily: 'Manrope', color: '#A3D4D4' }}>
        · Tap to update
      </span>
      <button
        data-testid="sw-update-apply"
        onClick={applyUpdate}
        disabled={updating}
        className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
        style={{ backgroundColor: '#8B5CF6', fontFamily: 'Manrope', boxShadow: '0 2px 8px rgba(139,92,246,0.4)' }}
      >
        <RefreshCw className={`w-3 h-3 ${updating ? 'animate-spin' : ''}`} />
        {updating ? 'Updating…' : 'Update'}
      </button>
      <button
        data-testid="sw-update-dismiss"
        onClick={dismiss}
        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors"
        style={{ backgroundColor: 'rgba(163,212,212,0.1)', color: '#A3D4D4' }}
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>,
    document.body
  );
}
